import React from 'react';
import { Link } from 'react-router-dom';
import { Article } from '../../types';
import { formatBlogDate, formatReadingTime } from '../../utils/blogFormat';
import BlogIcon from './BlogIcon';

interface ArticleCardProps {
  article: Article;
  /** ترتیب در گرید برای lazy/eager بودن تصویر */
  index?: number;
}

/**
 * کارت مقاله در شبکهٔ «همه مقالات».
 * دسکتاپ: بج دسته روی تصویر، عنوان، خلاصه، «تاریخ • زمان مطالعه».
 * موبایل: ردیف دسته/تاریخ بالای عنوان و فوتر زمان مطالعه + فلش.
 */
const ArticleCard: React.FC<ArticleCardProps> = ({ article, index = 0 }) => {
  const href = `/articles/${article.slug}`;
  const date = formatBlogDate(article.publishedAt || article.createdAt);
  const reading = formatReadingTime(article.readingTime);
  const categoryName = article.category?.name;
  // خلاصهٔ مخصوص کارت (در طرح با خلاصهٔ اصلی مقاله فرق دارد)
  const summary = article.cardSummary || article.excerpt;

  return (
    <article className="flex flex-col overflow-hidden rounded-[12px] border border-[#e9e8e5] bg-white transition-shadow sm:shadow-[0px_1px_3px_0px_rgba(30,32,34,0.04)] duration-200 hover:shadow-[0px_6px_18px_0px_rgba(30,32,34,0.08)]">
      <Link to={href} className="relative block aspect-[16/9] w-full overflow-hidden rounded-t-[11px] bg-[#efeeeb]" aria-label={article.title}>
        {article.featuredImage ? (
          <img
            src={article.featuredImage}
            alt={article.featuredImageAlt || article.title}
            loading={index < 3 ? 'eager' : 'lazy'}
            decoding="async"
            className="h-full w-full object-cover transition-transform duration-500 hover:scale-[1.03]"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-[12px] text-[#52575e]">بدون تصویر</div>
        )}

        {/* دسکتاپ: بج دسته (راست‌بالا) */}
        {categoryName && (
          <span className="absolute right-3 top-3 hidden rounded-[6px] border border-[#e9e8e5] bg-white/90 px-[10px] py-1 text-[12px] font-medium leading-4 text-[#52575e] backdrop-blur-[4px] sm:inline-flex">
            {categoryName}
          </span>
        )}

        {/* دسکتاپ: بج «همراه ویدیو» (چپ‌بالا) */}
        {article.hasVideo && (
          <span className="absolute left-3 top-3 hidden items-center gap-1 rounded-[6px] bg-[#331751] px-2 py-[2px] text-[12px] font-medium leading-4 text-white drop-shadow-[0px_1px_1px_rgba(0,0,0,0.05)] sm:inline-flex">
            <BlogIcon name="video-white" width={14} height={14} />
            همراه ویدیو
          </span>
        )}

        {/* موبایل: بج «همراه ویدیو» روشن (راست‌بالا) */}
        {article.hasVideo && (
          <span className="absolute right-[10px] top-[10px] inline-flex items-center gap-1 rounded-[4px] bg-[rgba(250,249,246,0.9)] px-2 py-[2px] text-[12px] font-medium leading-5 text-[#331751] backdrop-blur-[2px] sm:hidden">
            <BlogIcon name="video-badge-purple" width={11.667} height={9.333} />
            همراه ویدیو
          </span>
        )}
      </Link>

      <div className="flex flex-1 flex-col gap-[6px] p-[14px] sm:gap-2 sm:p-5">
        {/* موبایل: دسته و تاریخ */}
        <div className="flex items-center justify-between text-[12px] font-medium leading-5 sm:hidden">
          <span className="text-[#331751]">{categoryName || 'مقاله'}</span>
          <span className="text-[#5b5f66]">{date}</span>
        </div>

        <h3 className="blog-clamp-2 text-[18px] font-bold leading-[24.75px] text-[#1a1c1a]">
          <Link to={href} className="transition-colors hover:text-[#331751]">
            {article.title}
          </Link>
        </h3>

        {summary && <p className="blog-clamp-2 min-h-[51.5px] text-[14px] sm:min-h-[45.5px] leading-[22.75px] text-[#5b5f66] sm:text-[#52575e]">{summary}</p>}

        {/* دسکتاپ: تاریخ • زمان مطالعه */}
        <div className="mt-auto hidden pt-1 text-[12px] font-medium leading-4 text-[#52575e] sm:block">
          {date} • {reading}
        </div>

        {/* موبایل: فوتر زمان مطالعه + فلش */}
        <div className="mt-auto flex items-center justify-between border-t border-[#efeeeb] pt-[10px] sm:hidden">
          <span className="inline-flex items-center gap-1 text-[12px] font-medium leading-5 text-[#5b5f66]">
            <BlogIcon name="clock" width={12.5} height={12.5} />
            {reading}
          </span>
          {/* ناحیهٔ لمس بزرگ‌تر بدون تغییر ارتفاع ردیف طرح */}
          <Link to={href} aria-label={`مطالعه ${article.title}`} className="-m-2 inline-flex items-center justify-center rounded-md p-2">
            <BlogIcon name="arrow-left" width={12} height={9} />
          </Link>
        </div>
      </div>
    </article>
  );
};

export default ArticleCard;
